import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Shield, Users, Building2, Plus, Search, Loader2, Trash2, Edit3,
  Database, AlertTriangle, X, CheckCircle, UserPlus
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SECURITY_CLEARANCES = ["None", "Confidential", "Secret", "Top Secret"];
const GENDERS = ["Male", "Female", "Other"];
const APPOINTMENT_TYPES = ["Permanent", "Contract", "Secondment", "Internship", "Temporary"];

const emptyMember = {
  // Personal Identification
  surname: "", first_name: "", middle_name: "", date_of_birth: "", place_of_birth: "",
  gender: "", nationality: "", id_number: "", passport_number: "",
  // Contact Information
  email: "", phone: "", mobile: "", physical_address: "", postal_address: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  // Membership Details
  employee_number: "", appointment_date: "", confirmation_date: "", current_appointment: "",
  appointment_type: "", pension_number: "", tax_id: "",
  // Professional & Role
  department: "", division: "", section: "", position_id: "", supervisor_id: "",
  level_code: "", skills: "", qualifications: "", certifications: "", languages: "",
  // Admin & Compliance
  security_clearance: "", clearance_expiry: "", medical_clearance_date: "", medical_expiry: "",
  last_performance_rating: "", disciplinary_notes: "", training_completed: "",
};

export default function HRAdminPanel() {
  const [levels, setLevels] = useState([]);
  const [positions, setPositions] = useState([]);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState(emptyMember);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [memberSection, setMemberSection] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [levelsRes, positionsRes, membersRes] = await Promise.all([
        axios.get(`${API}/api/hr/organization/levels`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/api/hr/positions`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/api/hr/members`, { headers: getHeaders(), withCredentials: true }),
      ]);
      setLevels(levelsRes.data);
      setPositions(positionsRes.data);
      setMembers(membersRes.data);
    } catch (err) {
      toast.error("Failed to load HR data");
    }
    setIsLoading(false);
  };

  const handleGenerateSeed = async () => {
    if (!window.confirm("Generate the full 8-level organisation structure (~44,000 positions)? This will replace all existing positions.")) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/hr/organisation/generate-seed`, {},
        { headers: getHeaders(), withCredentials: true }
      );
      setSeedResult(res.data);
      toast.success(`Created ${res.data.total_positions} positions`);
      fetchData();
    } catch (err) {
      toast.error("Failed to generate organisation: " + (err.response?.data?.detail || err.message));
    }
    setGenerating(false);
  };

  const handleMemberField = (field, value) => {
    setMemberForm(prev => ({ ...prev, [field]: value }));
  };

  const resetMemberForm = () => {
    setMemberForm(emptyMember);
    setEditingMember(null);
    setMemberSection(0);
  };

  const openEditMember = (member) => {
    setEditingMember(member);
    setMemberForm({
      surname: member.surname || "",
      first_name: member.first_name || "",
      middle_name: member.middle_name || "",
      date_of_birth: member.date_of_birth || "",
      place_of_birth: member.place_of_birth || "",
      gender: member.gender || "",
      nationality: member.nationality || "",
      id_number: member.id_number || "",
      passport_number: member.passport_number || "",
      email: member.email || "",
      phone: member.phone || "",
      mobile: member.mobile || "",
      physical_address: member.physical_address || "",
      postal_address: member.postal_address || "",
      emergency_contact_name: member.emergency_contact_name || "",
      emergency_contact_phone: member.emergency_contact_phone || "",
      employee_number: member.employee_number || "",
      appointment_date: member.appointment_date || "",
      confirmation_date: member.confirmation_date || "",
      current_appointment: member.current_appointment || "",
      appointment_type: member.appointment_type || "",
      pension_number: member.pension_number || "",
      tax_id: member.tax_id || "",
      department: member.department || "",
      division: member.division || "",
      section: member.section || "",
      position_id: member.position_id || "",
      supervisor_id: member.supervisor_id || "",
      level_code: member.level_code || "",
      skills: Array.isArray(member.skills) ? member.skills.join(", ") : (member.skills || ""),
      qualifications: Array.isArray(member.qualifications) ? member.qualifications.join(", ") : (member.qualifications || ""),
      certifications: member.certifications || "",
      languages: member.languages || "",
      security_clearance: member.security_clearance || "",
      clearance_expiry: member.clearance_expiry || "",
      medical_clearance_date: member.medical_clearance_date || "",
      medical_expiry: member.medical_expiry || "",
      last_performance_rating: member.last_performance_rating || "",
      disciplinary_notes: member.disciplinary_notes || "",
      training_completed: member.training_completed || "",
    });
    setShowAddMember(true);
  };

  const handleSaveMember = async () => {
    setSaving(true);
    try {
      const payload = {
        ...memberForm,
        name: `${memberForm.first_name} ${memberForm.surname}`.trim(),
        skills: memberForm.skills.split(",").map(s => s.trim()).filter(Boolean),
        qualifications: memberForm.qualifications.split(",").map(s => s.trim()).filter(Boolean),
      };

      if (editingMember) {
        await axios.put(`${API}/api/hr/members/${editingMember.id}`, payload,
          { headers: getHeaders(), withCredentials: true }
        );
        toast.success("Member updated");
      } else {
        await axios.post(`${API}/api/hr/members`, payload,
          { headers: getHeaders(), withCredentials: true }
        );
        toast.success("Member created");
      }
      setShowAddMember(false);
      resetMemberForm();
      fetchData();
    } catch (err) {
      toast.error("Failed to save member: " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm("Delete this member?")) return;
    try {
      await axios.delete(`${API}/api/hr/members/${memberId}`,
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success("Member deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete member");
    }
  };

  const filteredMembers = members.filter(m => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (m.name || "").toLowerCase().includes(q)
      || (m.employee_number || "").toLowerCase().includes(q)
      || (m.email || "").toLowerCase().includes(q)
      || (m.department || "").toLowerCase().includes(q);
  });

  const SECTION_LABELS = [
    "Personal Identification",
    "Contact Information",
    "Membership Details",
    "Professional & Role",
    "Admin & Compliance"
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-['Barlow_Condensed'] font-bold text-slate-800 tracking-wide">
          HR Admin Panel
        </h1>
        <p className="text-sm text-slate-500">Manage organisation structure and member profiles</p>
      </div>

      <Tabs defaultValue="organogram" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="organogram" className="data-[state=active]:bg-white">
            <Building2 className="w-4 h-4 mr-2" />
            Organogram Manager
          </TabsTrigger>
          <TabsTrigger value="members" className="data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" />
            Member Manager
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Organogram Manager */}
        <TabsContent value="organogram" className="space-y-4">
          <Card className="bg-white border-slate-200">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-700">Organisation Levels</h2>
              <Button
                onClick={handleGenerateSeed}
                disabled={generating}
                className="bg-gradient-to-r from-amber-500 to-orange-600 text-white"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                {generating ? "Generating..." : "Generate Organisation"}
              </Button>
            </div>
            <div className="p-4">
              {seedResult && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-1">
                    <CheckCircle className="w-4 h-4" /> Organisation Generated
                  </div>
                  <p className="text-sm text-green-600">
                    {seedResult.total_positions?.toLocaleString()} positions created across {seedResult.levels_created} levels.
                    HR Admin: {seedResult.hr_admin_email} / {seedResult.hr_admin_password}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {levels.map((level) => (
                  <Card key={level.code} className="p-3 border-slate-200 bg-app">
                    <div className="flex items-center justify-between">
                      <Badge className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">{level.code}</Badge>
                      <span className="text-xs text-slate-400">Level {level.order}</span>
                    </div>
                    <p className="mt-2 font-medium text-slate-700 text-sm">{level.name}</p>
                    <p className="text-xs text-slate-400">{level.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Tab 2: Member Manager */}
        <TabsContent value="members" className="space-y-4">
          <Card className="bg-white border-slate-200">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search members..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button onClick={() => { resetMemberForm(); setShowAddMember(true); }} className="bg-emerald-500 text-white">
                <UserPlus className="w-4 h-4 mr-2" /> Add Member
              </Button>
            </div>
            <ScrollArea className="max-h-[60vh]">
              <table className="w-full text-sm">
                <thead className="bg-app text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Employee #</th>
                    <th className="text-left p-3">Department</th>
                    <th className="text-left p-3">Level</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredMembers.length === 0 && (
                    <tr><td colSpan={6} className="text-center p-8 text-slate-400">No members found</td></tr>
                  )}
                  {filteredMembers.map((m) => (
                    <tr key={m.id} className="hover:bg-app">
                      <td className="p-3 font-medium text-slate-700">{m.name || m.first_name + " " + m.surname}</td>
                      <td className="p-3 text-slate-500">{m.employee_number || "-"}</td>
                      <td className="p-3 text-slate-500">{m.department || "-"}</td>
                      <td className="p-3">{m.level_code ? <Badge variant="outline">{m.level_code}</Badge> : "-"}</td>
                      <td className="p-3 text-slate-500">{m.email || "-"}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditMember(m)}>
                            <Edit3 className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteMember(m.id)}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={(open) => { if (!open) resetMemberForm(); setShowAddMember(open); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-500" />
              {editingMember ? "Edit Member" : "Add New Member"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {/* Section tabs */}
            <div className="flex gap-1 flex-wrap">
              {SECTION_LABELS.map((label, idx) => (
                <button
                  key={idx}
                  onClick={() => setMemberSection(idx)}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    memberSection === idx
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Section 1: Personal Identification */}
            {memberSection === 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">1</span>
                  Personal Identification
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Surname *</Label>
                    <Input value={memberForm.surname} onChange={(e) => handleMemberField("surname", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">First Name *</Label>
                    <Input value={memberForm.first_name} onChange={(e) => handleMemberField("first_name", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Middle Name</Label>
                    <Input value={memberForm.middle_name} onChange={(e) => handleMemberField("middle_name", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Date of Birth</Label>
                    <Input type="date" value={memberForm.date_of_birth} onChange={(e) => handleMemberField("date_of_birth", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Place of Birth</Label>
                    <Input value={memberForm.place_of_birth} onChange={(e) => handleMemberField("place_of_birth", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Gender</Label>
                    <Select value={memberForm.gender} onValueChange={(v) => handleMemberField("gender", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nationality</Label>
                    <Input value={memberForm.nationality} onChange={(e) => handleMemberField("nationality", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">ID Number</Label>
                    <Input value={memberForm.id_number} onChange={(e) => handleMemberField("id_number", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Passport Number</Label>
                    <Input value={memberForm.passport_number} onChange={(e) => handleMemberField("passport_number", e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            {/* Section 2: Contact Information */}
            {memberSection === 1 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">2</span>
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Email *</Label>
                    <Input type="email" value={memberForm.email} onChange={(e) => handleMemberField("email", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Phone</Label>
                    <Input value={memberForm.phone} onChange={(e) => handleMemberField("phone", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Mobile</Label>
                    <Input value={memberForm.mobile} onChange={(e) => handleMemberField("mobile", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Physical Address</Label>
                    <Input value={memberForm.physical_address} onChange={(e) => handleMemberField("physical_address", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Postal Address</Label>
                    <Input value={memberForm.postal_address} onChange={(e) => handleMemberField("postal_address", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Emergency Contact Name</Label>
                    <Input value={memberForm.emergency_contact_name} onChange={(e) => handleMemberField("emergency_contact_name", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Emergency Contact Phone</Label>
                    <Input value={memberForm.emergency_contact_phone} onChange={(e) => handleMemberField("emergency_contact_phone", e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            {/* Section 3: Membership Details */}
            {memberSection === 2 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">3</span>
                  Membership-Specific Details
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Employee Number *</Label>
                    <Input value={memberForm.employee_number} onChange={(e) => handleMemberField("employee_number", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Appointment Date</Label>
                    <Input type="date" value={memberForm.appointment_date} onChange={(e) => handleMemberField("appointment_date", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Confirmation Date</Label>
                    <Input type="date" value={memberForm.confirmation_date} onChange={(e) => handleMemberField("confirmation_date", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Current Appointment</Label>
                    <Input value={memberForm.current_appointment} onChange={(e) => handleMemberField("current_appointment", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Appointment Type</Label>
                    <Select value={memberForm.appointment_type} onValueChange={(v) => handleMemberField("appointment_type", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {APPOINTMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Pension Number</Label>
                    <Input value={memberForm.pension_number} onChange={(e) => handleMemberField("pension_number", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tax ID</Label>
                    <Input value={memberForm.tax_id} onChange={(e) => handleMemberField("tax_id", e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            {/* Section 4: Professional & Role */}
            {memberSection === 3 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">4</span>
                  Professional & Organizational Role
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Department</Label>
                    <Input value={memberForm.department} onChange={(e) => handleMemberField("department", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Division</Label>
                    <Input value={memberForm.division} onChange={(e) => handleMemberField("division", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Section</Label>
                    <Input value={memberForm.section} onChange={(e) => handleMemberField("section", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Level Code</Label>
                    <Select value={memberForm.level_code} onValueChange={(v) => handleMemberField("level_code", v)}>
                      <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
                      <SelectContent>
                        {levels.map(l => <SelectItem key={l.code} value={l.code}>{l.code} - {l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Position</Label>
                    <Select value={memberForm.position_id} onValueChange={(v) => handleMemberField("position_id", v)}>
                      <SelectTrigger><SelectValue placeholder="Select position" /></SelectTrigger>
                      <SelectContent>
                        {positions.slice(0, 200).map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.post_number || p.title} - {p.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Supervisor</Label>
                    <Select value={memberForm.supervisor_id} onValueChange={(v) => handleMemberField("supervisor_id", v)}>
                      <SelectTrigger><SelectValue placeholder="Select supervisor" /></SelectTrigger>
                      <SelectContent>
                        {members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Skills (comma-separated)</Label>
                    <Input value={memberForm.skills} onChange={(e) => handleMemberField("skills", e.target.value)} placeholder="Leadership, Communication, Data Analysis" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Qualifications (comma-separated)</Label>
                    <Input value={memberForm.qualifications} onChange={(e) => handleMemberField("qualifications", e.target.value)} placeholder="BSc, MSc, PhD" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Certifications</Label>
                    <Input value={memberForm.certifications} onChange={(e) => handleMemberField("certifications", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Languages</Label>
                    <Input value={memberForm.languages} onChange={(e) => handleMemberField("languages", e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            {/* Section 5: Admin & Compliance */}
            {memberSection === 4 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">5</span>
                  Administrative & Compliance
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Security Clearance</Label>
                    <Select value={memberForm.security_clearance} onValueChange={(v) => handleMemberField("security_clearance", v)}>
                      <SelectTrigger><SelectValue placeholder="Select clearance" /></SelectTrigger>
                      <SelectContent>
                        {SECURITY_CLEARANCES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Clearance Expiry</Label>
                    <Input type="date" value={memberForm.clearance_expiry} onChange={(e) => handleMemberField("clearance_expiry", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Medical Clearance Date</Label>
                    <Input type="date" value={memberForm.medical_clearance_date} onChange={(e) => handleMemberField("medical_clearance_date", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Medical Expiry</Label>
                    <Input type="date" value={memberForm.medical_expiry} onChange={(e) => handleMemberField("medical_expiry", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Last Performance Rating</Label>
                    <Input value={memberForm.last_performance_rating} onChange={(e) => handleMemberField("last_performance_rating", e.target.value)} />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Disciplinary Notes</Label>
                    <Textarea value={memberForm.disciplinary_notes} onChange={(e) => handleMemberField("disciplinary_notes", e.target.value)} rows={2} />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Training Completed</Label>
                    <Textarea value={memberForm.training_completed} onChange={(e) => handleMemberField("training_completed", e.target.value)} rows={2} />
                  </div>
                </div>
              </div>
            )}

            {/* Section navigation buttons */}
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <Button
                variant="outline"
                onClick={() => setMemberSection(Math.max(0, memberSection - 1))}
                disabled={memberSection === 0}
              >
                Previous
              </Button>
              <span className="text-xs text-slate-400">{memberSection + 1} / {SECTION_LABELS.length}</span>
              <Button
                variant="outline"
                onClick={() => setMemberSection(Math.min(SECTION_LABELS.length - 1, memberSection + 1))}
                disabled={memberSection === SECTION_LABELS.length - 1}
              >
                Next
              </Button>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowAddMember(false); resetMemberForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleSaveMember} disabled={saving} className="bg-emerald-500 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingMember ? "Update Member" : "Create Member"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
